import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Snackbar, Alert, CircularProgress, TextField, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import DownloadIcon from '@mui/icons-material/Download';

const API_URL = process.env.REACT_APP_API_URL;

function AdminSalaryAdvanceDashboard({ getToken, apiBase }) {
  const tokenGetter = getToken || (() => localStorage.getItem('gogig_admin_access'));
  const baseUrl = apiBase ? `${API_URL}/${apiBase}` : `${API_URL}/api/admin`;
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
  const [approveDialog, setApproveDialog] = useState({ open: false, request: null, approvedAmount: '' });

  useEffect(() => {
    fetchRequests();
  }, [selectedMonth]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = typeof tokenGetter === 'function' ? tokenGetter() : tokenGetter;
      const res = await fetch(`${baseUrl}/salary-advance/admin/report?month=${selectedMonth}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.success) setRequests(data.requests);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load salary advance requests', severity: 'error' });
    }
    setLoading(false);
  };

  const handleApproveClick = (request) => {
    setApproveDialog({ open: true, request, approvedAmount: request.amount != null ? String(request.amount) : '' });
  };

  const handleApproveConfirm = async () => {
    const { request, approvedAmount } = approveDialog;
    if (!request) return;
    const amt = approvedAmount && !isNaN(parseFloat(approvedAmount)) ? parseFloat(approvedAmount) : null;
    try {
      const token = typeof tokenGetter === 'function' ? tokenGetter() : tokenGetter;
      const res = await fetch(`${baseUrl}/salary-advance/admin/${request.id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: 'approved', approved_amount: amt })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to update status');
      const emailMsg = data.emailSent
        ? 'Approved. Email sent to fin@eduvantage.in, head@eduvantage.in, abhishek.pandit@eduvantage.in and SPOC(s).'
        : (data.emailError
          ? `Approved but email could not be sent. Ask admin to set SMTP in server .env (SMTP_HOST, SMTP_USER, SMTP_PASS). Error: ${data.emailError}`
          : 'Approved successfully.');
      setSnackbar({ open: true, message: emailMsg, severity: data.emailSent ? 'success' : 'warning' });
      setApproveDialog({ open: false, request: null, approvedAmount: '' });
      fetchRequests();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    if (newStatus === 'approved') {
      const req = requests.find(r => r.id === requestId);
      if (req) handleApproveClick(req);
      return;
    }
    try {
      const token = typeof tokenGetter === 'function' ? tokenGetter() : tokenGetter;
      const res = await fetch(`${baseUrl}/salary-advance/admin/${requestId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to update status');
      setSnackbar({ open: true, message: 'Status updated successfully', severity: 'success' });
      fetchRequests();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleDownload = async () => {
    try {
      const token = typeof tokenGetter === 'function' ? tokenGetter() : tokenGetter;
      const res = await fetch(`${baseUrl}/salary-advance/admin/report/download?month=${selectedMonth}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to download report');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `salary-advance-report-${selectedMonth}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={800} mb={3} color="primary.main" textAlign="center">
        💰 Salary Advance Requests
      </Typography>

      <Card sx={{ mb: 3, bgcolor: '#f8fafc' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                type="month"
                label="Select Month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                fullWidth
              >
                Download Report
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Candidate</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Approved Amount</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">No requests found</TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{new Date(request.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>{request.candidate_name}</TableCell>
                    <TableCell>{request.company}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color: request.type === 'NAPS' ? '#22c55e' :
                            request.type === 'Bvoc' ? '#3498db' : '#f39c12',
                          fontWeight: 600
                        }}
                      >
                        {request.type}
                      </Typography>
                    </TableCell>
                    <TableCell>₹{request.amount}</TableCell>
                    <TableCell>{request.approved_amount != null ? `₹${request.approved_amount}` : '—'}</TableCell>
                    <TableCell>{request.reason}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color: request.status === 'approved' ? 'success.main' :
                            request.status === 'rejected' ? 'error.main' : 'warning.main',
                          fontWeight: 600
                        }}
                      >
                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {request.status === 'pending' && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleStatusChange(request.id, 'approved')}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={() => handleStatusChange(request.id, 'rejected')}
                          >
                            Reject
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={approveDialog.open} onClose={() => setApproveDialog({ open: false, request: null, approvedAmount: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Salary Advance</DialogTitle>
        <DialogContent>
          {approveDialog.request && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Candidate: <strong>{approveDialog.request.candidate_name}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Requested: ₹{approveDialog.request.amount}
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Approved Amount"
                type="number"
                fullWidth
                value={approveDialog.approvedAmount}
                onChange={(e) => setApproveDialog(prev => ({ ...prev, approvedAmount: e.target.value }))}
                placeholder="Enter approved amount"
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog({ open: false, request: null, approvedAmount: '' })}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApproveConfirm}>
            Save & Approve
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AdminSalaryAdvanceDashboard; 